import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes, faSpinner, faUserShield, faCookieBite, faKey, faShieldAlt,
  faCircleInfo, faServer, faCheckCircle, faArrowLeft, faUser
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import authProfileService from '../../services/authProfileService';

const ROLE_OPTIONS = [
  { value: 'superadmin', label: 'Super Admin' },
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'user', label: 'User' },
  { value: 'read_only', label: 'Read-only' },
  { value: 'guest', label: 'Guest' }
];

/**
 * CreateProfileFromCaptureModal
 *
 * Lists the distinct authenticated sessions captured in this target's proxy
 * traffic and turns the chosen one into an AuthProfile — extracting its cookies /
 * Authorization / auth headers. This is the reliable path for logins that the
 * auto-detector misses (ServiceNow SSO, multi-redirect flows).
 */
const CreateProfileFromCaptureModal = ({ webTarget, isDarkMode, onClose, onCreated }) => {
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null); // chosen candidate
  const [name, setName] = useState('');
  const [role, setRole] = useState('user');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!webTarget?._id) return;
      setLoading(true);
      setError(null);
      try {
        const res = await authProfileService.getCaptureCandidates(webTarget._id);
        if (!cancelled) setCandidates(res?.candidates || []);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || err.message || 'Failed to load captured sessions');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [webTarget?._id]);

  const chooseCandidate = (c) => {
    setSelected(c);
    setName(c.detectedUsername ? `${c.detectedUsername}` : `${c.host} session`);
    setRole(c.suggestedRole || 'user');
  };

  const handleCreate = async () => {
    if (!selected || !name.trim()) return;
    setSaving(true);
    try {
      await authProfileService.createFromCapture({
        projectId: webTarget.project,
        webTargetId: webTarget._id,
        exchangeId: selected.id,
        name: name.trim(),
        role
      });
      toast.success(`Profile "${name.trim()}" created from captured session`);
      onCreated?.();
      onClose?.();
    } catch (err) {
      toast.error('Failed to create profile: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const panel = isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const muted = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const heading = isDarkMode ? 'text-gray-100' : 'text-gray-900';
  const rowBase = isDarkMode ? 'border-gray-700 hover:bg-gray-700/40' : 'border-gray-200 hover:bg-gray-50';
  const inputClass = `w-full px-3 py-2 border rounded text-sm ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-800'}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className={`w-full max-w-3xl max-h-[88vh] flex flex-col rounded-lg border shadow-2xl ${panel}`} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={`flex items-center justify-between px-4 py-3 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faUserShield} className="text-indigo-400" />
            <span className={`text-sm font-semibold ${heading}`}>New profile from login capture</span>
          </div>
          <button onClick={onClose} className={`p-1.5 rounded ${isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <p className={`text-xs mb-3 flex items-start gap-1.5 ${muted}`}>
            <FontAwesomeIcon icon={faCircleInfo} className="mt-0.5" />
            <span>
              Log in through the proxy as each user (different roles), then pick that session below.
              The full authenticated session (cookies + auth headers, including ServiceNow's <span className="font-mono">X-UserToken</span>) is captured into a reusable profile.
            </span>
          </p>

          {/* Loading / error / empty */}
          {loading ? (
            <div className={`py-12 text-center ${muted}`}>
              <FontAwesomeIcon icon={faSpinner} spin className="text-2xl mb-2" />
              <p className="text-sm">Scanning captured traffic…</p>
            </div>
          ) : error ? (
            <div className={`p-3 rounded border text-sm ${isDarkMode ? 'border-red-800 bg-red-900/20 text-red-300' : 'border-red-200 bg-red-50 text-red-700'}`}>
              {error}
            </div>
          ) : candidates.length === 0 ? (
            <div className={`py-10 text-center ${muted}`}>
              <FontAwesomeIcon icon={faServer} className="text-2xl mb-2" />
              <p className="text-sm font-medium">No authenticated sessions found in captured traffic</p>
              <p className="text-xs mt-1">Start the proxy, log into the app as a user, browse one authenticated page, then reopen this dialog.</p>
            </div>
          ) : !selected ? (
            /* Candidate list */
            <div className="space-y-2">
              {candidates.map(c => (
                <button
                  key={c.id}
                  onClick={() => chooseCandidate(c)}
                  className={`w-full text-left p-3 rounded border transition-colors ${rowBase}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <FontAwesomeIcon icon={faUser} className={c.detectedUsername ? 'text-green-400' : muted} />
                      <span className={`text-sm font-medium truncate ${heading}`}>
                        {c.detectedUsername || 'Unknown user'}
                      </span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                        {c.suggestedRole}
                      </span>
                    </div>
                    <span className={`text-[10px] ${muted}`}>{c.requestCount} req</span>
                  </div>
                  <div className={`text-[11px] font-mono truncate ${muted}`}>{c.host}</div>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    {c.artifacts.cookieCount > 0 && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1 ${isDarkMode ? 'bg-orange-900/30 text-orange-300' : 'bg-orange-100 text-orange-700'}`}>
                        <FontAwesomeIcon icon={faCookieBite} /> {c.artifacts.cookieCount} cookie{c.artifacts.cookieCount !== 1 ? 's' : ''}
                      </span>
                    )}
                    {c.artifacts.hasBearer && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1 ${isDarkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700'}`}>
                        <FontAwesomeIcon icon={faKey} /> Bearer
                      </span>
                    )}
                    {c.artifacts.hasCustomToken && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1 ${isDarkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                        <FontAwesomeIcon icon={faShieldAlt} /> {c.artifacts.customTokenNames?.[0] || 'token'}
                      </span>
                    )}
                    {c.artifacts.sessionCookieNames?.length > 0 && (
                      <span className={`text-[10px] font-mono ${muted}`}>
                        {c.artifacts.sessionCookieNames.slice(0, 3).join(', ')}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            /* Confirmation form */
            <div className="space-y-4">
              <button onClick={() => setSelected(null)} className={`text-xs flex items-center gap-1.5 ${muted} hover:underline`}>
                <FontAwesomeIcon icon={faArrowLeft} /> Back to sessions
              </button>

              <div className={`p-3 rounded border ${isDarkMode ? 'border-gray-700 bg-gray-900/40' : 'border-gray-200 bg-gray-50'}`}>
                <div className={`text-[11px] font-mono break-all ${muted}`}>
                  <span className={heading}>{selected.method}</span> {selected.sampleUrl}
                </div>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {selected.artifacts.cookieCount > 0 && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-orange-900/30 text-orange-300' : 'bg-orange-100 text-orange-700'}`}>
                      {selected.artifacts.cookieCount} cookies
                    </span>
                  )}
                  {selected.artifacts.hasBearer && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700'}`}>Authorization header</span>
                  )}
                  {selected.artifacts.customTokenNames?.map(n => (
                    <span key={n} className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${isDarkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>{n}</span>
                  ))}
                </div>
              </div>

              <div>
                <label className={`block text-xs font-medium mb-1 ${muted}`}>Profile name</label>
                <input value={name} onChange={e => setName(e.target.value)} className={inputClass} placeholder="e.g. admin.user" autoFocus />
              </div>

              <div>
                <label className={`block text-xs font-medium mb-1 ${muted}`}>Role</label>
                <select value={role} onChange={e => setRole(e.target.value)} className={inputClass}>
                  {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
                <p className={`text-[10px] mt-1 ${muted}`}>
                  Role drives the AuthSmash matrix ordering (baseline vs lower-privilege). Pick this user's real privilege level.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {selected && !loading && (
          <div className={`flex items-center justify-end gap-2 px-4 py-3 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <button onClick={onClose} className={`px-4 py-2 text-sm font-medium ${muted} hover:underline`}>Cancel</button>
            <button
              onClick={handleCreate}
              disabled={saving || !name.trim()}
              className="px-5 py-2 bg-indigo-600 text-white rounded text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
            >
              <FontAwesomeIcon icon={saving ? faSpinner : faCheckCircle} spin={saving} />
              Create profile
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateProfileFromCaptureModal;
